import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import AgentLoadingRipple from './AgentLoadingRipple';
import useRotatingStatusWord from '../../hooks/useRotatingStatusWord';

/**
 * Shared one-liner loading strip (TD-2334 / TD-2341): blue ripple dot · elapsed seconds ·
 * grey box naming the tool · a rotating, on-brand "spinner word". Purely presentational — the
 * caller resolves the (frozen) tool label and the (locale + family) word list. Driven by
 * AgentToolLoadingStrip for both the company searches and every other tool, so all agent
 * tool-loading looks and behaves identically.
 *
 * a11y: a native <output> (implicit role=status) live region whose only announced content is a
 * static cue; the dot/elapsed/word are decorative or update ~1 Hz, so they are aria-hidden.
 */

const TICK_MS = 1000;
const SECONDS_PER_MINUTE = 60;

function formatElapsed(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / TICK_MS));
  if (totalSeconds < SECONDS_PER_MINUTE) {
    return `${totalSeconds}s`;
  }
  const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  return `${minutes}m ${seconds}s`;
}

const AgentToolLoadingRow = ({ startTime = null, toolLabel, words, srLabel = 'Loading…' }) => {
  const word = useRotatingStatusWord(words);
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (!startTime) {
      setElapsedMs(0);
      return undefined;
    }
    const tick = () => setElapsedMs(Date.now() - startTime);
    tick();
    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, [startTime]);

  return (
    <output className="agent-message-loading agent-search-loading-row">
      <span className="agent-search-sr-only">{srLabel}</span>
      <AgentLoadingRipple />
      {startTime ? (
        <span className="agent-search-elapsed" aria-hidden="true">{formatElapsed(elapsedMs)}</span>
      ) : null}
      <div className="agent-search-tool-box" aria-hidden="true">
        <span className="agent-search-tool-text">{toolLabel}</span>
      </div>
      {/* Rotating word last: as the most variable-width element, only the right edge moves. */}
      <span className="agent-search-word" aria-hidden="true">{word}</span>
    </output>
  );
};

AgentToolLoadingRow.propTypes = {
  /** Epoch ms when work started; drives the elapsed timer. Null hides the timer. */
  startTime: PropTypes.number,
  /** Pre-resolved (frozen) human label for the grey tool box. */
  toolLabel: PropTypes.string.isRequired,
  /** Pre-resolved rotating word list for the current locale + family. */
  words: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** Screen-reader-only static cue announced by the live region. */
  srLabel: PropTypes.string
};

export default AgentToolLoadingRow;
